import { ReloadIcon } from "@radix-ui/react-icons"
import { Button, type ButtonProps, Flex, Text } from "@radix-ui/themes"
import type { ButtonHTMLAttributes, ReactNode } from "react"
import { cn } from "../../utils/cn"

interface ButtonCustomProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
  children?: ReactNode
  variant?: "primary" | "secondary" | "base" | "soft" | "solid" | "danger"
  color?: ButtonProps["color"]
  variantRadix?: ButtonProps["variant"]
  size?: "sm" | "base" | "lg"
  fullWidth?: boolean
  isLoading?: boolean
}

export const ButtonCustom = ({
  children,
  variant = "primary",
  color,
  size = "base",
  variantRadix,
  fullWidth,
  disabled,
  isLoading = false,
  className,
  ...rest
}: ButtonCustomProps) => {
  let radixButtonVariant: ButtonProps["variant"]
  let radixButtonColor: ButtonProps["color"]
  switch (variant) {
    case "primary":
      radixButtonVariant = undefined
      break
    case "secondary":
      radixButtonVariant = "outline"
      radixButtonColor = "gray"
      break
    case "base":
      radixButtonVariant = "solid"
      radixButtonColor = "gray"
      break
    case "danger":
      radixButtonVariant = "outline"
      radixButtonColor = "red"
      break
  }
  radixButtonColor = color ?? radixButtonColor
  radixButtonVariant = variantRadix ?? radixButtonVariant

  let radixButtonSize: ButtonProps["size"] | undefined
  switch (size) {
    case "sm":
      radixButtonSize = "1"
      break
    case "base":
      break
    case "lg":
      radixButtonSize = "4"
      break
  }

  // Use custom VEAX styling for primary buttons
  if (variant === "primary") {
    return (
      <button
        disabled={disabled || isLoading}
        className={cn(
          "relative w-full h-14 font-medium text-[13px] uppercase overflow-hidden transition-all duration-350",
          "bg-gradient-to-r from-[#F6FF8E] via-[#FFB1E9] to-[#1F36FE]",
          "text-black",
          disabled && "opacity-50 cursor-not-allowed",
          !disabled && !isLoading && "cursor-pointer",
          fullWidth && "w-full",
          className
        )}
        style={{
          clipPath: "polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))"
        }}
        {...rest}
      >
        {isLoading ? (
          <span className="relative z-10 flex items-center justify-center gap-2">
            <ReloadIcon className="size-5 animate-spin" />
            {children}
          </span>
        ) : (
          <span className="relative z-10">{children}</span>
        )}
      </button>
    )
  }

  return (
    <Flex align="center" gap="2" asChild>
      <Button
        color={radixButtonColor}
        variant={radixButtonVariant}
        size={radixButtonSize}
        disabled={disabled || isLoading}
        className={cn(
          !disabled && "cursor-pointer",
          { sm: "h-8", base: "h-10", lg: "h-14" }[size],
          className
        )}
        {...rest}
      >
        {isLoading ? <ReloadIcon className="size-5 animate-spin" /> : null}
        <Text weight="bold">{children}</Text>
      </Button>
    </Flex>
  )
}
